import React, { useMemo } from 'react';
import { Box, Card, CardContent, Grid, Typography, Button, CircularProgress, ButtonBase } from '@mui/material';
import { ShoppingCart as CartIcon } from '@mui/icons-material';
import { compatibilityMatrix } from '../services/compatibilityMatrix';

function isConnectorReferenceLabel(t) {
  if (!t) return false;
  if (t.linkedConnectorId) return true;
  if (typeof t.id === 'string' && t.id.startsWith('label_')) return true;
  const c = String(t.content || '');
  return /^#\d+\s*\|/.test(c);
}

function formatFinishingSummary(finishing) {
  if (!finishing || typeof finishing !== 'object') return '';

  const parts = [];

  const heatShrink = finishing.heatShrink;
  if (heatShrink) {
    const getVal = (k) => {
      const v = heatShrink?.[k] ?? heatShrink?.global;
      if (!v || v === 'none') return null;
      return v;
    };
    const endA = getVal('endA');
    const endB = getVal('endB');

    if (endA || endB) {
      const hsParts = [];
      if (endA) hsParts.push(`EndA: ${endA}`);
      if (endB) hsParts.push(`EndB: ${endB}`);
      parts.push(`HeatShrink(${hsParts.join(', ')})`);
    }
  }

  const codingRings = finishing.codingRings;
  if (codingRings) {
    const getVal = (k) => {
      const v = codingRings?.[k] ?? codingRings?.global;
      if (!v || v === 'none') return null;
      return v;
    };
    const endA = getVal('endA');
    const endB = getVal('endB');
    if (endA || endB) {
      const crParts = [];
      if (endA) crParts.push(`EndA: ${endA}`);
      if (endB) crParts.push(`EndB: ${endB}`);
      parts.push(`CodingRings(${crParts.join(', ')})`);
    }
  }

  const anyCustom = Object.entries(finishing.heatShrink || {}).some(
    ([k, v]) => k !== 'global' && (v === 'custom' || v === 'clear+custom')
  );
  if (anyCustom && finishing.customHeatShrink) {
    const ch = finishing.customHeatShrink;
    if (ch.mode === 'logo') parts.push('CustomPrint(logo)');
    else if (ch.mode === 'text') {
      const l1 = (ch.line1 || '').trim();
      const l2 = (ch.line2 || '').trim();
      if (l1 || l2) parts.push(`CustomPrint("${[l1, l2].filter(Boolean).join(' / ')}")`);
      else parts.push('CustomPrint(text)');
    }
  }

  return parts.join(' • ');
}

export default function PanelReviewCart({
  panelConfig,
  onAddToCart,
  addingToCart,
  proofImageUrl
}) {
  const nlfxColors = {
    secondary: '#b32b2b'
  };
  const connectors = panelConfig?.connectors || [];
  const wireTailings = panelConfig?.wireTailings || [];
  const engravedCustomText = useMemo(
    () => (panelConfig?.customText || []).filter((t) => !isConnectorReferenceLabel(t)),
    [panelConfig?.customText]
  );

  const rows = useMemo(() => {
    return connectors.map((connector) => {
      const tailing = wireTailings.find((t) => t.connectorId === connector.id);
      const endBConnectorId = tailing?.noTail ? null : tailing?.connectors?.endB?.connector;
      const endBConnectorData = endBConnectorId ? compatibilityMatrix[endBConnectorId] : null;

      const lengthInFeet = tailing && !tailing.noTail ? (tailing.length / 12).toFixed(1) : '0';
      const lengthDisplay = lengthInFeet.endsWith('.0') ? lengthInFeet.slice(0, -2) : lengthInFeet;

      return {
        connector,
        tailing,
        endBName: tailing?.noTail ? 'No wire tail' : (endBConnectorData?.name || endBConnectorData?.description || 'Not configured'),
        lengthDisplay,
        finishingSummary: tailing?.noTail ? '' : formatFinishingSummary(tailing?.finishing)
      };
    });
  }, [connectors, wireTailings]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Panel
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Panel Configuration
              </Typography>

              <Typography variant="body2">
                <strong>Size:</strong> {panelConfig.size.width}" × {panelConfig.size.height}"
              </Typography>
              <Typography variant="body2">
                <strong>Material:</strong>{' '}
                {panelConfig.material === 'flanged-aluminum' ? 'Flanged Aluminum' : 'Black Anodized'}
              </Typography>
              <Typography variant="body2">
                <strong>Connectors:</strong> {connectors.length}
              </Typography>

              {rows.map(({ connector, tailing, endBName, lengthDisplay, finishingSummary }, index) => (
                <Box
                  key={connector.id}
                  sx={{ ml: 2, mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}
                >
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {index + 1}. {connector.type} Panel Connector
                  </Typography>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Grid Location: ({connector.position.row}, {connector.position.col})
                  </Typography>

                  {tailing?.noTail ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Wire tail: No wire tail
                    </Typography>
                  ) : tailing ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Tail Connector: {endBName}
                      </Typography>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Wire Type: {tailing.wireType || 'Not configured'}
                      </Typography>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Tail Length: {lengthDisplay}ft
                      </Typography>

                      {finishingSummary ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Finishing: {finishingSummary}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Finishing: None
                        </Typography>
                      )}

                      {tailing.specialInstructions ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontStyle: 'italic' }}
                        >
                          Special Instructions: {tailing.specialInstructions}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
              ))}

              <Typography variant="body2">
                <strong>Custom Logo:</strong>{' '}
                {panelConfig.customLogo ? (
                  <ButtonBase
                    component="a"
                    href={panelConfig.customLogo.proofDataUrl || panelConfig.customLogo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: nlfxColors.secondary, textDecoration: 'underline' }}
                  >
                    Yes (view)
                  </ButtonBase>
                ) : (
                  'No'
                )}
              </Typography>
              <Typography variant="body2">
                <strong>Custom Text:</strong> {engravedCustomText.length} items
              </Typography>
              {engravedCustomText.length > 0 && (
                <Box sx={{ mt: 1, ml: 2 }}>
                  {engravedCustomText.map((t) => (
                    <Typography
                      key={t.id}
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.25 }}
                    >
                      • {t.content}
                    </Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pricing
              </Typography>
              <Typography variant="h4" color="error">
                ${panelConfig.totalPrice.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Panel blank + setup fee + per-hole/etch + connectors + wire tailings + customizations
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Proof
              </Typography>
              {proofImageUrl ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Click the proof to view full size.
                  </Typography>
                  <ButtonBase
                    component="a"
                    href={proofImageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: '100%',
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 2,
                      overflow: 'hidden',
                      backgroundColor: '#fff',
                      display: 'block'
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        // Give the preview enough height to show the full panel proof without cropping.
                        height: { xs: 220, sm: 300, md: 360 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fff'
                      }}
                    >
                      <img
                        src={proofImageUrl}
                        alt="Panel proof"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          display: 'block'
                        }}
                      />
                    </Box>
                  </ButtonBase>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  A proof image will appear here after you click Next from the build step, or when you add to cart.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={addingToCart ? <CircularProgress size={20} /> : <CartIcon />}
          onClick={onAddToCart}
          disabled={addingToCart || connectors.length === 0}
          sx={{
            flex: 1,
            backgroundColor: nlfxColors.secondary,
            '&:hover': { backgroundColor: '#8b2222' },
            borderRadius: 2,
            py: 1.25,
            textTransform: 'none',
            fontWeight: 700
          }}
          title="Add to Cart"
        >
          {addingToCart ? 'Adding to Cart...' : panelConfig.totalPrice > 0 ? (
            <>Add to Cart – ${panelConfig.totalPrice.toFixed(2)}</>
          ) : (
            'Price unavailable'
          )}
        </Button>
      </Box>
    </Box>
  );
}

